import { useState, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import apiWithRefresh from "../utils/apiWithRefresh";
import Api from "../utils/api";
import { handleResendPaymentToken as resendPaymentToken } from "../utils/paymentActions";
import { Helmet } from "react-helmet-async";
import BlockHeader from "../components/BlockHeader";
import statusEnum from "../utils/statusEnum";
import FeesTypeEnum from "../utils/FeesTypeEnum";

// Razorpay integration
const loadRazorpayScript = () => {
  console.log("Loading Razorpay SDK...");
  return new Promise((resolve) => {
    if (document.getElementById("razorpay-sdk")) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.id = "razorpay-sdk";
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const PaymentForm = ({
  enrollmentId,
  amount,
  onSuccess,
  paymentType,
  token,
}) => {
  const [enrollment, setEnrollment] = useState(null);
  const [enrollmentError, setEnrollmentError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedCountryId, setSelectedCountryId] = useState("");
  const [countryCurrencyList, setCountryCurrencyList] = useState([]);
  const [feePlans, setFeePlans] = useState([]);
  const [selectedFeePlanId, setSelectedFeePlanId] = useState("");
  const [paymentAmount, setPaymentAmount] = useState(amount || "");
  const [selectedCurrency, setSelectedCurrency] = useState("..loading");
  const [resendLoading, setResendLoading] = useState(false);
  const [resendError, setResendError] = useState(null);
  const [resendSuccess, setResendSuccess] = useState(null);
  useEffect(() => {
    Api.get("countrycurrency").then((data) => setCountryCurrencyList(data));
    Api.get("fee-plans").then((data) => setFeePlans(data));
  }, []);
  const [fetchAttempted, setFetchAttempted] = useState(false);
  useEffect(() => {
    async function fetchEnrollment() {
      try {
        let url = `learner-enrollments/${enrollmentId}/criteria`;
        if (token) url += `?token=${encodeURIComponent(token)}`;
        const data = await Api.get(url);
        setEnrollment(data);
        setSelectedFeePlanId((data && data.feePlanId) || "");
      } catch (err) {
        setEnrollmentError(err.message || "Failed to fetch enrollment.");
      } finally {
        setFetchAttempted(true);
      }
    }
    if (enrollmentId) fetchEnrollment();
    else setFetchAttempted(true);
  }, [enrollmentId, token, amount]);

  function getPaymentDetails() {
    const selectedCountry = countryCurrencyList.find(
      (c) => c.id.toString() === selectedCountryId,
    );
    return {
      amount: paymentAmount,
      currency: selectedCountry ? selectedCountry.code : "INR",
      feePlanId: selectedFeePlanId,
    };
  }
  // Set default country if enrollment has countryId
  useEffect(() => {
    if (enrollment && enrollment.countryId) {
      setSelectedCountryId(enrollment.countryId.toString());
    }
  }, [enrollment]);

  // Keep selectedCurrency in sync with selected country
  useEffect(() => {
    const selectedCountry = countryCurrencyList.find(
      (c) => c.id.toString() === selectedCountryId,
    );
    setSelectedCurrency(selectedCountry ? selectedCountry.code : "INR");
  }, [countryCurrencyList, selectedCountryId]);

  // For cash enrollment, update amount/currency on fee plan change
  useEffect(() => {
    if (enrollment && enrollment.isCashEnrollment && selectedFeePlanId) {
      const plan = feePlans.find((p) => p.id.toString() === selectedFeePlanId);
      if (plan) {
        setPaymentAmount(plan.amount);
        setSelectedCurrency(plan.currency || "INR");
      }
    }
  }, [enrollment, selectedFeePlanId, feePlans]);

  // Always use enrollmentId from props/URL, not from criteria API
  async function handleResendPaymentToken() {
    setResendLoading(true);
    setResendError(null);
    setResendSuccess(null);
    try {
      // enrollmentId is always from props/URL
      if (!enrollmentId) {
        throw new Error("Enrollment ID is missing.");
      }
      const res = await resendPaymentToken(enrollmentId);
      if (res && res.paymentLink) {
        setResendSuccess(
          "A new payment link has been generated. Redirecting...",
        );
        setTimeout(() => {
          window.location.href = res.paymentLink;
        }, 1500);
      } else {
        setResendError(
          "Failed to generate a new payment link. Please try again later.",
        );
      }
    } catch (err) {
      setResendError(err.message || "Failed to generate a new payment link.");
    } finally {
      setResendLoading(false);
    }
  }
  useEffect(() => {
    const fetchAmountOrMonthlyFees = async () => {
      if (!enrollment || !selectedCountryId) return;
      if (enrollment.isCashEnrollment) {
        if (!selectedFeePlanId) return;
        try {
          const apiendpoint = `fee-plan-prices?feePlanId=${selectedFeePlanId}&countryCurrencyId=${selectedCountryId}`;
          const res = await Api.get(apiendpoint);
          console.log("Fetched amount from fee-plan-prices:", res);
          if (Array.isArray(res) && res.length > 0 && res[0].amount) {
            setPaymentAmount(res[0].amount);
            const currencyCode =
              res[0].countryCurrency?.code ||
              res[0].countryCurrencyCode ||
              res[0].currency;
            if (currencyCode) {
              setSelectedCurrency(currencyCode);
            }
          } else if (res && res.amount) {
            setPaymentAmount(res.amount);
            const currencyCode =
              res.countryCurrency?.code ||
              res.countryCurrencyCode ||
              res.currency;
            if (currencyCode) {
              setSelectedCurrency(currencyCode);
            }
          } else {
            setPaymentAmount(0);
          }
        } catch (err) {
          setPaymentAmount(0);
        }
        return;
      }

      if (
        enrollment.nextPayment?.type === "admission" ||
        enrollment.nextPayment?.type === "special-chance"
      ) {
        let configAppId = enrollment.nextPayment.type === "admission" ? 1 : 2;
        if (enrollment.isQrEnrollment) {
          configAppId = 3; // QR Enrollment configAppId
        }
        try {
          const apiendpoint = `configAppPrices?configAppId=${configAppId}&countryCurrencyId=${selectedCountryId}`;
          const res = await Api.get(apiendpoint);
          if (Array.isArray(res) && res.length > 0 && res[0].amount) {
            setPaymentAmount(res[0].amount);
            const currencyCode =
              res[0].countryCurrency?.code ||
              res[0].countryCurrencyCode ||
              res[0].currency;
            if (currencyCode) {
              setSelectedCurrency(currencyCode);
            }
          } else if (res && res.amount) {
            setPaymentAmount(res.amount);
            const currencyCode =
              res.countryCurrency?.code ||
              res.countryCurrencyCode ||
              res.currency;
            if (currencyCode) {
              setSelectedCurrency(currencyCode);
            }
          } else {
            setPaymentAmount(0);
          }
        } catch (err) {
          setPaymentAmount(0);
        }
      } else {
        setPaymentAmount(0);
      }
    };
    fetchAmountOrMonthlyFees();
  }, [enrollment, selectedCountryId, selectedFeePlanId]);

  async function handleRazorpayPayment() {
    setLoading(true);
    setError(null);
    setSuccess(null);
    const paymentDetails = getPaymentDetails();
    try {
      if (!paymentDetails.feePlanId) {
        setError("Please select a fee plan.");
        setLoading(false);
        return;
      }
      // loadRazorpayScript will return true/false based on whether the script loaded successfully or not
      const res = await loadRazorpayScript();
      if (!res) {
        setError("Failed to load Razorpay SDK.");
        console.error("Razorpay SDK failed to load.");
        setLoading(false);
        return;
      }
      let orderData;
      const numericAmount =
        typeof paymentDetails.amount === "string"
          ? parseFloat(paymentDetails.amount)
          : paymentDetails.amount;
      let endpoint = "";
      const payType =
        paymentType || enrollment?.nextPayment?.type || FeesTypeEnum.ADMISSION;
      // Cash Enrollment: call trigger-first-month-payment
      if (enrollment && enrollment.isCashEnrollment) {
        endpoint = `learner-enrollments/${enrollmentId}/trigger-first-month-payment`;
      } else if (payType === FeesTypeEnum.ADMISSION) {
        endpoint = `learner-enrollments/${enrollmentId}/admission-fee`;
      } else if (payType === FeesTypeEnum.SPECIAL_CHANCE) {
        endpoint = `learner-enrollments/${enrollmentId}/special-chance-fee`;
      } else {
        endpoint = `learner-enrollments/${enrollmentId}/admission-fee`;
      }
      const payload = {
        amount: numericAmount,
        currency: paymentDetails.currency,
        receipt: `order_${enrollmentId}`,
        feePlanId: paymentDetails.feePlanId,
        type: payType,
      };

      if (token) {
        orderData = await Api.post(
          endpoint + `?token=${encodeURIComponent(token)}`,
          payload,
        );
      } else {
        orderData = await apiWithRefresh.post(endpoint, payload);
      }
      console.log("Payment endpoint response:", orderData);
      const order = orderData && orderData.data;
      if (!order || !order.id) {
        setError("Failed to initialize payment. Please try again.");
        console.error(
          "Payment endpoint did not return valid order id.",
          orderData,
        );
        setLoading(false);
        return;
      }
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || "rzp_test_xxxxxxxxxxxxx",
        amount: order.amount,
        currency: order.currency,
        name: process.env.APPLICATION_NAME || "Gurmat Education Platform",
        description: enrollment.isCashEnrollment
          ? "First month payment"
          : "Enrollment Payment",
        order_id: order.id,
        handler: function (response) {
          // You can verify the payment on the server side here by sending the payment details to your backend
          setSuccess("Payment successful!");
          if (onSuccess) onSuccess(response);
          
        },
        prefill: {
          email: enrollment?.email || "",
          name: enrollment?.learnerName || "",
        },
        notes: {
          enrollmentId: enrollmentId,
        },
        theme: {
          color: "#1890ff",
        },
      };
      // Initialize Razorpay payment
      const rzp = new window.Razorpay(options);
      // Handle payment failure
      rzp.on("payment.failed", function (response) {
        setError(response.error.description || "Payment failed.");
        console.error("Razorpay payment failed:", response);
      });
      rzp.open();
    } catch (err) {
      setError(err.message || "Payment failed.");
      console.error("Payment initialization error:", err);
    } finally {
      setLoading(false);
    }
  }

  if (
    enrollmentError &&
    (enrollmentError.toLowerCase().includes("token") ||
      enrollmentError.toLowerCase().includes("expired"))
  ) {
    return (
      <div
        style={{
          minHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <BlockHeader
          title="Payment Error"
          icon="❌"
          subtitle={enrollmentError}
        />
        <button
          className="btn"
          style={{ marginTop: 24, fontWeight: 700 }}
          onClick={handleResendPaymentToken}
          disabled={resendLoading}
        >
          {resendLoading ? (
            <span>
              <span
                className="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
                style={{ marginRight: 8 }}
              ></span>
              Generating new link...
            </span>
          ) : (
            <span>
              <span style={{ fontSize: 22, marginRight: 8 }}>🔗</span>Resend
              Payment Link
            </span>
          )}
        </button>
        {resendError && (
          <div style={{ color: "#b71c1c", marginTop: 16 }}>{resendError}</div>
        )}
        {resendSuccess && (
          <div style={{ color: "#388e3c", marginTop: 16 }}>{resendSuccess}</div>
        )}
      </div>
    );
  }

  if (!enrollment && fetchAttempted) {
    return (
      <div
        style={{
          minHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <BlockHeader
          title="No Enrollment"
          icon="📄"
          subtitle="No enrollment data found. (Check if the enrollmentId in the URL is valid and the backend is running.)"
        />
      </div>
    );
  }

  if (
    enrollment &&
    ((enrollment.batchAllocated && !enrollment.isCashEnrollment) ||
      enrollment.accountActivated)
  ) {
    return (
      <div
        style={{
          minHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <BlockHeader
          title="Enrollment Status"
          icon="✅"
          subtitle={
            enrollment.batchAllocated && enrollment.accountActivated
              ? "Your enrollment is complete and your account is active."
              : enrollment.batchAllocated
                ? "Batch has been allocated. Please check your email for further instructions."
                : "Your account is already activated."
          }
        />
      </div>
    );
  }

  if (enrollment && enrollment.status === statusEnum.PAYMENT_COMPLETED) {
    return (
      <div
        style={{
          minHeight: "80vh",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <BlockHeader
          title="Payment Already Completed"
          icon="💸"
          subtitle="Your payment has already been received. Thank you! Please check your email for confirmation and next steps."
        />
      </div>
    );
  }

  if (success) {
    return (
      <BlockHeader
        title="Payment Successful"
        icon="✔️"
        subtitle={
          "Thank you! Your payment was processed successfully. Please check your email for confirmation and next steps."
        }
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>AKAAL KIDS | Enrollment Payment</title>
      </Helmet>
      <BlockHeader
        title={
          "Enrollment Payment - " +
          (enrollment && enrollment.nextPayment
            ? enrollment.isCashEnrollment &&
              enrollment.nextPayment.type === "monthly"
              ? "First Month Fee Plan"
              : enrollment.nextPayment.type === FeesTypeEnum.ADMISSION
                ? "Admission Fee"
                : "Special Chance Fee"
            : "")
        }
        icon="💳"
        subtitle="Complete your payment below."
      />
      {/* Learner Details Section */}
      {enrollment && (
        <div
          style={{
            margin: "0 auto 24px auto",
            padding: "24px 32px",
            background: "linear-gradient(135deg, #e3f2fd 0%, #f8fafc 100%)",
            borderRadius: 18,
            maxWidth: 600, // Match Payment Summary width
            fontSize: 18,
            boxShadow: "0 4px 24px #90caf933",
            border: "2px solid #90caf9",
            display: "flex",
            flexDirection: "column",
            gap: 10,
            position: "relative",
            width: "100%", // Ensure it fills up to maxWidth
          }}
        >
          <div
            style={{
              fontSize: 28,
              fontWeight: 700,
              color: "#1976d2",
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span role="img" aria-label="student">
              🎓
            </span>
            Learner Details
          </div>
          <hr
            style={{
              border: "none",
              borderTop: "2px dashed #90caf9",
              margin: "8px 0 16px 0",
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 22 }}>👤</span>
            <span>
              <b>Name:</b> {enrollment.learnerName}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 22 }}>✉️</span>
            <span>
              <b>Email:</b> {enrollment.email}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 22 }}>🌏</span>
            <span>
              <b>Country:</b> {enrollment.country}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 22 }}>📋</span>
            <span>
              <b>Status:</b> {enrollment.status.replace(/-/g, " ")}
            </span>
          </div>
          {enrollment.nextPayment && (
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span style={{ fontSize: 22 }}>💸</span>
              <span>
                <b>Payment Stage:</b> {enrollment.nextPayment.label}
              </span>
            </div>
          )}
        </div>
      )}
      {/* Payment Summary Section */}
      <div
        style={{
          width: 600,
          maxWidth: "90%",
          margin: "40px auto",
          padding: 32,
          background: "#fff",
          borderRadius: 18,
          boxShadow: "0 6px 32px #0050b322",
          position: "relative",
          transition: "box-shadow 0.3s",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          gap: 32,
        }}
      >
        {/* Country and Fee Plan selection for payment */}
        {enrollment && enrollment.status === statusEnum.PENDING_PAYMENT && (
          <div style={{ marginBottom: 0, marginTop: 16 }}>
            <p style={{ fontSize: 14, color: "#130303", marginTop: 4 }}>
              Month auto debit will be applicable based on fee plan selection.
            </p>

            <select
              id="feePlanId"
              name="feePlanId"
              className="form-control"
              value={selectedFeePlanId}
              onChange={(e) => setSelectedFeePlanId(e.target.value)}
              style={{ marginTop: 6 }}
            >
              <option value="">Choose Monthly Fee Plan</option>
              {feePlans.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name}
                </option>
              ))}
            </select>
          </div>
        )}
        {/* Payment summary always shown */}
        {/* Show PaymentSummaryCard for cash enrollment on fee plan selection */}
        {enrollment &&
          enrollment.status === statusEnum.PENDING_PAYMENT &&
          enrollment.isCashEnrollment &&
          selectedFeePlanId &&
          (paymentAmount === "" ||
          paymentAmount === undefined ||
          paymentAmount === null ? (
            <div
              style={{
                textAlign: "center",
                margin: "32px 0",
                fontSize: 20,
                color: "#888",
              }}
            >
              Loading fee plan details...
            </div>
          ) : (
            <PaymentSummaryCard
              label={enrollment.nextPayment.label}
              amount={paymentAmount}
              currency={selectedCurrency}
              name={"Fee Plan Payment"}
            />
          ))}
        {/* Default: Show PaymentSummaryCard for other payment types */}
        {enrollment &&
          enrollment.status === statusEnum.PENDING_PAYMENT &&
          !enrollment.isCashEnrollment &&
          enrollment.nextPayment && (
            <PaymentSummaryCard
              label={
                enrollment.nextPayment.label || enrollment.nextPayment.type
              }
              amount={paymentAmount}
              currency={selectedCurrency}
              name={enrollment.nextPayment.label || enrollment.nextPayment.type}
            />
          )}

        {/* Pay Now button */}
        {enrollment &&
          enrollment.status === statusEnum.PENDING_PAYMENT &&
          enrollment.nextPayment && (
            <button
              className="btn w-100"
              type="button"
              style={{
                fontWeight: 700,
                fontSize: 19,
                padding: "12px 0",
                borderRadius: 10,
                letterSpacing: 0.5,
                background: "linear-gradient(90deg, #1890ff 0%, #38f9d7 100%)",
                color: "#fff",
                border: "none",
                boxShadow: "0 2px 8px #1890ff33",
                transition: "background 0.2s, box-shadow 0.2s",
                cursor: loading ? "not-allowed" : "pointer",
                position: "relative",
              }}
              disabled={loading}
              onClick={handleRazorpayPayment}
            >
              {loading ? (
                <span>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                    style={{ marginRight: 8 }}
                  ></span>
                  Processing...
                </span>
              ) : (
                <span>
                  <span style={{ fontSize: 22, marginRight: 8 }}>💳</span>
                  Pay Now
                </span>
              )}
            </button>
          )}
        {/* Fallback message if nothing else matches */}
        {enrollment && !enrollment.nextPayment && (
          <div style={{ textAlign: "center", color: "#888", marginTop: 40 }}>
            Please contact support for further instructions.
          </div>
        )}
        {/* Error message */}
        {error && (
          <div
            style={{
              marginTop: 18,
              background: "#fff0f0",
              color: "#b71c1c",
              borderRadius: 8,
              padding: "12px 18px",
              fontWeight: 600,
              boxShadow: "0 2px 8px #b71c1c22",
              display: "flex",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span style={{ fontSize: 22 }}>❌</span> {error}
          </div>
        )}
      </div>
    </>
  );
};

// PaymentSummaryCard: common summary for both admission/special-chance and countryId
const PaymentSummaryCard = ({ label, amount, currency, name }) => (
  <div
    style={{
      background:
        "radial-gradient(circle at 60% 40%, #e6f7ff 0%, #c3eaff 100%)",
      borderRadius: 20,
      padding: "32px 36px 28px 36px",
      marginBottom: 32,
      fontWeight: 900,
      fontSize: 26,
      color: "#1890ff",
      textAlign: "center",
      boxShadow: "0 8px 32px #1890ff55",
      border: "3px solid #91d5ff",
      letterSpacing: 2,
      position: "relative",
      transition: "box-shadow 0.3s",
      overflow: "hidden",
      zIndex: 2,
      minWidth: 320,
      maxWidth: 400,
      marginLeft: "auto",
      marginRight: "auto",
      backgroundClip: "padding-box",
      boxSizing: "border-box",
    }}
  >
    <span
      style={{
        display: "inline-block",
        marginBottom: 8,
        verticalAlign: "middle",
      }}
    >
      <span
        style={{
          fontSize: 28,
          marginRight: 8,
          verticalAlign: "middle",
        }}
      >
        💳
      </span>
      <span
        style={{
          fontSize: 22,
          color: "#0050b3",
          fontWeight: 800,
          letterSpacing: 1.5,
          textShadow: "0 1px 0 #fff",
          verticalAlign: "middle",
        }}
      >
        Payment Summary
      </span>
    </span>
    <hr
      style={{
        border: "none",
        borderTop: "2px dashed #91d5ff",
        margin: "12px 0 18px 0",
      }}
    />
    <span
      style={{
        fontSize: 38,
        color: "#0050b3",
        fontWeight: 900,
        letterSpacing: 2,
        textShadow: "0 2px 8px #b3e0ff",
        display: "block",
        marginBottom: 10,
      }}
    >
      {currency ? currency.toUpperCase() : "USD"} {amount}
    </span>
    <div
      style={{
        fontSize: 20,
        color: "#1890ff",
        marginTop: 8,
        fontWeight: 700,
        textShadow: "0 1px 0 #fff",
        letterSpacing: 1.5,
        marginBottom: 2,
      }}
    >
      {label || name}
    </div>
  </div>
);

// Accept paymentType and token as props for direct rendering, fallback to URL params or path params if not provided
const Payment = (props) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const params = useParams();
  // Try props, then query string, then path params
  const enrollmentId =
    props.enrollmentId ||
    searchParams.get("enrollmentId") ||
    params.enrollmentId ||
    params.id ||
    params["*"]?.split("/")[0];
  const paymentType = props.paymentType || searchParams.get("paymentType");
  const token = props.token || searchParams.get("token");
  return (
    <PaymentForm
      {...props}
      enrollmentId={enrollmentId}
      paymentType={paymentType}
      token={token}
    />
  );
};

export default Payment;
